import React, { useContext } from 'react';
import {
  Drawer, Avatar, List, Badge, Typography, theme,
} from 'antd';
import { CommentOutlined } from '@ant-design/icons';
import { useIntl } from 'react-intl';
import { StoreContext } from '../store/index';

const formatConvTime = timestamp => {
  if (!timestamp?.toDate) return '';
  const d = timestamp.toDate();
  const now = new Date();
  if (d.toDateString() === now.toDateString()) {
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }
  return d.toLocaleDateString([], { day: '2-digit', month: 'short' });
};

const ConversationsDrawer = ({ visible, onClose }) => {
  const store = useContext(StoreContext);
  const { user: [user], chat: [, openChat] } = store;
  const { token } = theme.useToken();
  const { formatMessage } = useIntl();

  const conversations = store.conversations?.[0] ?? [];

  const handleOpen = conv => {
    const recipientUid = conv.participants.find(uid => uid !== user.uid);
    openChat({
      conversationId: conv.id,
      recipientUid,
      recipientNickname: conv.participantNicknames?.[recipientUid] ?? '',
      recipientPhotoURL: conv.participantPhotos?.[recipientUid] ?? '',
      recipientToken: null,
    });
    onClose();
  };

  return (
    <Drawer
      title={formatMessage({ id: 'chat.conversations.title' })}
      placement="right"
      onClose={onClose}
      open={visible}
      styles={{ body: { padding: '12px 16px' } }}
    >
      {conversations.length === 0 ? (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '40px 0',
          gap: 8,
        }}>
          <CommentOutlined style={{ fontSize: 32, color: token.colorTextQuaternary }} />
          <Typography.Text type="secondary" style={{ fontSize: 13 }}>
            {formatMessage({ id: 'chat.no.conversations' })}
          </Typography.Text>
        </div>
      ) : (
        <List
          dataSource={conversations}
          renderItem={conv => {
            const recipientUid = conv.participants.find(uid => uid !== user.uid);
            const unread = conv.unreadCount?.[user.uid] ?? 0;
            const nickname = conv.participantNicknames?.[recipientUid] ?? '?';
            const photo = conv.participantPhotos?.[recipientUid] ?? '';

            return (
              <List.Item
                key={conv.id}
                style={{
                  padding: '10px 0',
                  borderBottom: `1px solid ${token.colorBorderSecondary}`,
                  cursor: 'pointer',
                }}
                onClick={() => handleOpen(conv)}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%' }}>
                  <Badge count={unread} size="small" offset={[0, 0]}>
                    <Avatar src={photo} size={40}>
                      {nickname[0]?.toUpperCase()}
                    </Avatar>
                  </Badge>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                      <Typography.Text strong style={{ fontSize: 13 }}>
                        {nickname}
                      </Typography.Text>
                      <Typography.Text type="secondary" style={{ fontSize: 11, flexShrink: 0, marginLeft: 8 }}>
                        {formatConvTime(conv.lastMessageAt)}
                      </Typography.Text>
                    </div>
                    <Typography.Text
                      type="secondary"
                      ellipsis
                      style={{ fontSize: 12, display: 'block', fontWeight: unread > 0 ? 600 : 400 }}
                    >
                      {conv.lastMessage || '…'}
                    </Typography.Text>
                  </div>
                </div>
              </List.Item>
            );
          }}
        />
      )}
    </Drawer>
  );
};

export default ConversationsDrawer;
